import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Chip,
  IconButton,
  Button,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  RadioButtonChecked as EnOceanButtonIcon,
  Person as PersonIcon,
  PlayCircle as TestIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { getLocationId } from '../../../utils/locationTransformers';

const SettingsTab = ({
  selectedLocation,
  enoceanButtons,
  loadingButtons,
  buttonError,
  setButtonError,
  onDeleteButton,
  onOpenButtonTest,
  t
}) => {
  return (
    <Box>
      {/* EnOcean Buttons Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EnOceanButtonIcon color="primary" />
              <Typography variant="h6">EnOcean Buttons</Typography>
              <Chip
                size="small"
                label={enoceanButtons.length}
                color={enoceanButtons.length > 0 ? 'primary' : 'default'}
              />
            </Box>
            {enoceanButtons.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<TestIcon />}
                onClick={onOpenButtonTest}
              >
                Test Buttons
              </Button>
            )}
          </Box>

          {buttonError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setButtonError(null)}>
              {buttonError}
            </Alert>
          )}

          {loadingButtons ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : enoceanButtons.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <EnOceanButtonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No EnOcean buttons assigned to this location
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Use Button Discovery from a Hub device to register new buttons
              </Typography>
            </Box>
          ) : (
            <List dense>
              {enoceanButtons.map((button) => (
                <ListItem
                  key={button.id}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: 'background.paper'
                  }}
                >
                  <ListItemIcon>
                    <EnOceanButtonIcon color={button.buttonType === 'emergency' ? 'error' : 'primary'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">
                          {button.friendlyName || `Button ${button.hexCode}`}
                        </Typography>
                        <Chip
                          size="small"
                          label={button.buttonType || 'action'}
                          color={button.buttonType === 'emergency' ? 'error' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                        <Typography variant="caption" fontFamily="monospace">
                          HEX: {button.hexCode}
                        </Typography>
                        {button.person && (
                          <Chip size="small" icon={<PersonIcon />} label={button.person.friendlyName} variant="outlined" />
                        )}
                        <Chip
                          size="small"
                          label={button.status || 'unknown'}
                          color={button.status === 'online' ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Delete Button">
                      <IconButton edge="end" size="small" color="error" onClick={() => onDeleteButton(button)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Other Settings */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t ? t('LocationManagement.location_settings') : 'Location Settings'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t ? t('VisualTreeView.locationspecific_settings_permission') : 'Location-specific settings and permissions will be displayed here'}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SettingsTab;
